import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import type {
  PublicContent,
  Media,
  Plan,
  Leader,
  ChiefGuest,
  License,
  MediaType,
} from '@/types/public';

interface ApiEnvelope<T> {
  ok: true;
  data: T;
}

const unwrap = <T>(env: ApiEnvelope<T>): T => env.data;

/* --------------------------------- Queries -------------------------------- */

export const publicKeys = {
  content: ['public', 'content'] as const,
  media: (type?: MediaType) => ['public', 'media', type ?? 'image'] as const,
  plans: ['public', 'plans'] as const,
  licenses: ['public', 'licenses'] as const,
  leaders: ['public', 'leaders'] as const,
  chiefGuests: ['public', 'chief-guests'] as const,
};

export function usePublicContent() {
  return useQuery({
    queryKey: publicKeys.content,
    queryFn: async () => unwrap(await api.get<ApiEnvelope<PublicContent>>('/api/public/content')),
    staleTime: 60_000,
  });
}

export function usePublicMedia(type?: MediaType) {
  return useQuery({
    queryKey: publicKeys.media(type),
    queryFn: async () =>
      unwrap(
        await api.get<ApiEnvelope<{ items: Media[] }>>('/api/public/media', {
          query: { type },
        }),
      ),
    staleTime: 60_000,
  });
}

export function usePublicPlans() {
  return useQuery({
    queryKey: publicKeys.plans,
    queryFn: async () =>
      unwrap(await api.get<ApiEnvelope<{ items: Plan[] }>>('/api/public/plans')),
    staleTime: 5 * 60_000,
  });
}

export function usePublicLicenses() {
  return useQuery({
    queryKey: publicKeys.licenses,
    queryFn: async () =>
      unwrap(await api.get<ApiEnvelope<{ items: License[] }>>('/api/public/licenses')),
    staleTime: 5 * 60_000,
  });
}

export function usePublicLeaders() {
  return useQuery({
    queryKey: publicKeys.leaders,
    queryFn: async () =>
      unwrap(await api.get<ApiEnvelope<{ items: Leader[] }>>('/api/public/leaders')),
    staleTime: 5 * 60_000,
  });
}

export function usePublicChiefGuests() {
  return useQuery({
    queryKey: publicKeys.chiefGuests,
    queryFn: async () =>
      unwrap(await api.get<ApiEnvelope<{ items: ChiefGuest[] }>>('/api/public/chief-guests')),
    staleTime: 5 * 60_000,
  });
}

/* -------------------------------- Mutation -------------------------------- */

export interface ContactSubmitPayload {
  name: string;
  email: string;
  subject: string;
  message: string;
}

export function useContactSubmit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ContactSubmitPayload) =>
      unwrap(
        await api.post<ApiEnvelope<{ id: number; submitted: true }>>(
          '/api/public/contact',
          payload,
        ),
      ),
    onSuccess: () => {
      // Contact submissions may seed future admin inbox queries; keep as a no-op invalidation.
      qc.invalidateQueries({ queryKey: ['admin', 'contact-messages'] });
    },
  });
}
